import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  BookOpen, 
  ArrowLeft, 
  Search, 
  Filter, 
  ChevronDown,
  Loader2,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import BlogCard from './BlogCard';
import SEO from './SEO';
import { fetchAllPosts } from '../services/blogService';
import './BlogPage.css';

/**
 * Blog Archive Page Component
 * Lists all blog posts from the Growleads.io RSS feed
 * Includes search, filter, and sort functionality
 */
const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('newest'); // 'newest' | 'oldest'
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState(['all']);
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 9;

  useEffect(() => {
    const loadPosts = async () => {
      try {
        setLoading(true);
        const result = await fetchAllPosts();
        setPosts(result.posts);
        setFilteredPosts(result.posts);
        
        // Extract unique categories
        const allCategories = new Set(['all']);
        result.posts.forEach(post => {
          post.categories?.forEach(cat => allCategories.add(cat));
        });
        setCategories([...allCategories]);
        
        setError(null);
      } catch (err) {
        console.error('Failed to load blog posts:', err);
        setError('Unable to load blog posts. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadPosts();
    window.scrollTo(0, 0);
  }, []);

  // Filter and sort posts when criteria change
  useEffect(() => {
    let result = [...posts];
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(post => 
        post.title?.toLowerCase().includes(term) ||
        post.excerpt?.toLowerCase().includes(term) ||
        post.categories?.some(cat => cat.toLowerCase().includes(term))
      );
    }
    
    // Apply category filter
    if (selectedCategory !== 'all') {
      result = result.filter(post => 
        post.categories?.includes(selectedCategory)
      );
    }
    
    // Apply sort
    result.sort((a, b) => {
      const dateA = new Date(a.publishedAt || a.pubDate || 0);
      const dateB = new Date(b.publishedAt || b.pubDate || 0);
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });
    
    setFilteredPosts(result);
    setCurrentPage(1); // Reset to first page when filters change
  }, [posts, searchTerm, sortOrder, selectedCategory]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = filteredPosts.slice(indexOfFirstPost, indexOfLastPost);

  // Page navigation
  const goToPage = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages + 2) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      
      if (currentPage > 3) pages.push('...');
      
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = start; i <= end; i++) pages.push(i);
      
      if (currentPage < totalPages - 2) pages.push('...');
      
      pages.push(totalPages);
    }
    
    return pages;
  };

  return (
    <div className="blog-page">
      <SEO
        title="LinkedIn Profile Optimizer Blog - LinkedIn Optimization, AI Tools, and B2B Growth"
        description="Guides, frameworks, and playbooks from LinkedIn Profile Optimizer on LinkedIn headlines, About sections, outreach, and B2B lead generation."
        canonical="https://optimizer.growleads.io/blog"
        type="website"
      />

      {/* Header */}
      <header className="blog-page__header">
        <div className="container">
          <Link to="/" className="blog-page__back">
            <ArrowLeft size={18} />
            <span>Back to Home</span>
          </Link>
          
          <motion.div 
            className="blog-page__title-wrapper"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="blog-page__badge">
              <BookOpen size={16} />
              <span>LinkedIn Profile Optimizer</span>
            </div>
            <h1 className="blog-page__title">
              Frameworks for LinkedIn, Outreach, and B2B Growth
            </h1>
            <p className="blog-page__subtitle">
              Practical playbooks, copy templates, and audit-tested patterns from optimizing thousands of LinkedIn profiles.
            </p>
          </motion.div>
        </div>
      </header>

      {/* Filters */}
      <div className="blog-page__filters">
        <div className="container">
          <div className="blog-page__filters-inner">
            {/* Search */}
            <div className="blog-page__search">
              <Search size={18} />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label="Search blog posts"
              />
            </div>
            
            {/* Category Filter */}
            <div className="blog-page__filter-group">
              <Filter size={16} />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                aria-label="Filter by category"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>
                    {cat === 'all' ? 'All Categories' : cat}
                  </option>
                ))}
              </select>
              <ChevronDown size={14} className="select-arrow" />
            </div>
            
            {/* Sort */}
            <div className="blog-page__filter-group">
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                aria-label="Sort posts"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
              </select>
              <ChevronDown size={14} className="select-arrow" />
            </div>
          </div>
          
          {/* Results count */}
          {!loading && (
            <div className="blog-page__results-count">
              Showing {filteredPosts.length} of {posts.length} articles
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <main className="blog-page__content">
        <div className="container">
          {loading ? (
            <div className="blog-page__loading">
              <Loader2 size={32} className="spinner" />
              <p>Loading blog posts...</p>
            </div>
          ) : error ? (
            <div className="blog-page__error">
              <p>{error}</p>
              <button onClick={() => window.location.reload()}>
                Try Again
              </button>
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="blog-page__empty">
              <p>No articles found matching your criteria.</p>
              <button onClick={() => {
                setSearchTerm('');
                setSelectedCategory('all');
              }}>
                Clear Filters
              </button>
            </div>
          ) : (
            <>
              <div className="blog-page__grid">
                {currentPosts.map((post, index) => (
                  <BlogCard
                    key={post.link || index}
                    title={post.title}
                    excerpt={post.excerpt}
                    pubDate={post.pubDate}
                    featuredImage={post.featuredImage}
                    link={post.link}
                    categories={post.categories}
                    author={post.author}
                    index={index}
                  />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="blog-page__pagination">
                  <button 
                    className="pagination__btn pagination__btn--prev"
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    aria-label="Previous page"
                  >
                    <ChevronLeft size={18} />
                    <span>Previous</span>
                  </button>

                  <div className="pagination__pages">
                    {getPageNumbers().map((page, index) => (
                      page === '...' ? (
                        <span key={`ellipsis-${index}`} className="pagination__ellipsis">...</span>
                      ) : (
                        <button
                          key={page}
                          className={`pagination__page ${currentPage === page ? 'pagination__page--active' : ''}`}
                          onClick={() => goToPage(page)}
                          aria-label={`Go to page ${page}`}
                          aria-current={currentPage === page ? 'page' : undefined}
                        >
                          {page}
                        </button>
                      )
                    ))}
                  </div>

                  <button 
                    className="pagination__btn pagination__btn--next"
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    aria-label="Next page"
                  >
                    <span>Next</span>
                    <ChevronRight size={18} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Footer CTA */}
      <section className="blog-page__cta">
        <div className="container">
          <motion.div 
            className="blog-page__cta-inner"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2>Ready to Optimize Your LinkedIn Profile?</h2>
            <p>Get AI-powered recommendations to enhance your professional presence</p>
            <Link to="/optimize" className="blog-page__cta-btn">
              Start Free Audit
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Simple Footer */}
      <footer className="blog-page__footer">
        <div className="container">
          <p>
            © 2026 LinkedIn Profile Optimizer by{' '}
            <a href="https://growleads.io" target="_blank" rel="noopener noreferrer">
              Growleads.io
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default BlogPage;
